import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, Radio, PencilLine, Wifi, WifiOff, Router, Smartphone, ExternalLink, Info, Bell, QrCode } from 'lucide-react';
import {
  useGetBaseUnitTypesQuery,
  useGetBaseUnitListQuery,
  useAddBaseUnitMutation,
  useDeleteBaseUnitMutation,
  useUpdateBaseUnitMutation
} from '../services/baseUnitApi';
import { useGetBaseUnitTelemetryQuery } from '../services/baseUnitTelemetryApi';
import { formatDateTime } from '../utils/dateUtils';
import WifiConfigDialog from '../components/baseunits/WifiConfigDialog';
import BaseUnitTelemetryDialog from '../components/baseunits/BaseUnitTelemetryDialog';
import ArbSensorDialog from '../components/baseunits/ArbSensorDialog';
import BaseUnitAlarmDialog from '../components/baseunits/BaseUnitAlarmDialog';
import ProvisioningQRModal from '../components/baseunits/ProvisioningQRModal';
import { TS1_PORTAL } from '../config/ts1Portal';
import { usePermissions } from '../hooks/usePermissions';

interface EditBaseUnitState {
  id: number | null;
  name: string;
}

interface AddBaseUnitForm {
  serialNo: string;
  name: string;
  typeId: number | '';
}

const initialFormState: AddBaseUnitForm = {
  serialNo: '',
  name: '',
  typeId: ''
};

const NON_TS1_ONLINE_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

function CommsCell({ serialNo, type, lastContact }: { serialNo: number; type: number | null; lastContact: string | null }) {
  const isTS1 = type === 1;
  const { data: telemetry, isLoading } = useGetBaseUnitTelemetryQuery(
    serialNo.toString(),
    { skip: !isTS1 }
  );

  if (!isTS1) {
    const isOnline = !!lastContact && (Date.now() - new Date(lastContact).getTime()) < NON_TS1_ONLINE_WINDOW_MS;
    return (
      <div className="flex items-center">
        {isOnline ? (
          <>
            <Wifi className="h-4 w-4 text-green-500 dark:text-green-400" />
            <span className="ml-2 text-xs text-green-600 dark:text-green-400">Online</span>
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4 text-red-500 dark:text-red-400" />
            <span className="ml-2 text-xs text-red-600 dark:text-red-400">Offline</span>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center">
        <div className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-indigo-500 animate-spin" />
        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">Checking...</span>
      </div>
    );
  }

  const commsOnline = !!telemetry && (Date.now() - new Date(telemetry.timestamp).getTime()) < 60 * 60 * 1000;
  return (
    <div className="flex items-center">
      {commsOnline ? (
        <>
          <Wifi className="h-4 w-4 text-green-500 dark:text-green-400" />
          <span className="ml-2 text-xs text-green-600 dark:text-green-400">COMMS Online</span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4 text-red-500 dark:text-red-400" />
          <span className="ml-2 text-xs text-red-600 dark:text-red-400">Offline</span>
        </>
      )}
    </div>
  );
}

export default function BaseUnitsPage() {
  const { canManageBaseUnits } = usePermissions();
  const [isAddingBaseUnit, setIsAddingBaseUnit] = useState(false);
  const [formData, setFormData] = useState<AddBaseUnitForm>(initialFormState);
  const [editingBaseUnit, setEditingBaseUnit] = useState<EditBaseUnitState>({ id: null, name: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [wifiDialogOpen, setWifiDialogOpen] = useState(false);
  const [selectedBaseUnit, setSelectedBaseUnit] = useState<{ id: number; serialNo: number; base_unit_name: string } | null>(null);
  const [telemetryDialogOpen, setTelemetryDialogOpen] = useState(false);
  const [selectedTelemetryBaseUnit, setSelectedTelemetryBaseUnit] = useState<{ id: number; serialNo: number; base_unit_name: string; type: number } | null>(null);
  const [arbSensorDialogOpen, setArbSensorDialogOpen] = useState(false);
  const [selectedArbUnit, setSelectedArbUnit] = useState<{ serialNo: number; base_unit_name: string; silent_alarm?: boolean } | null>(null);
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [selectedAlarmUnit, setSelectedAlarmUnit] = useState<{ serialNo: number; base_unit_name: string; type: number } | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  const { data: baseUnits, isLoading, error } = useGetBaseUnitListQuery(undefined, { pollingInterval: 300000 });
  const { data: types, isLoading: typesLoading } = useGetBaseUnitTypesQuery();
  const [addBaseUnit] = useAddBaseUnitMutation();
  const [deleteBaseUnit] = useDeleteBaseUnitMutation();
  const [updateBaseUnit] = useUpdateBaseUnitMutation();

  const handleEdit = (baseUnit: { id: number; base_unit_name: string }) => {
    setEditingBaseUnit({ id: baseUnit.id, name: baseUnit.base_unit_name });
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBaseUnit.id) return;
    setFormError(null);

    try {
      await updateBaseUnit({
        baseUnitId: editingBaseUnit.id,
        baseUnitName: editingBaseUnit.name
      }).unwrap();
      
      setEditingBaseUnit({ id: null, name: '' });
    } catch (error) {
      setFormError('Failed to update base unit name. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingBaseUnit({ id: null, name: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.typeId) {
      setFormError('Please select a base unit type.');
      return;
    }

    try {
      const selectedType = types?.find(t => t.type === formData.typeId);

      const serialStripped = formData.serialNo.replace(/-/g, '');
      const serialNumber = parseInt(serialStripped, 10);
      if (isNaN(serialNumber) || serialNumber <= 0) {
        setFormError('Serial number must be a valid number (dashes are stripped automatically).');
        return;
      }
      await addBaseUnit({
        baseUnitSerialNo: serialNumber,
        baseUnitName: formData.name,
        typeId: formData.typeId as number,
        typeDescription: selectedType?.description
      }).unwrap();

      setFormData(initialFormState);
      setIsAddingBaseUnit(false);
    } catch (error) {
      if (error.status === 400) {
        setFormError('Base unit with same serial number or name already exists.');
      } else {
        setFormError('Failed to add base unit. Please try again.');
      }
    }
  };

  const handleDelete = async (baseUnit: { id: number }) => {
    if (window.confirm('Are you sure you want to delete this base unit?')) {
      try {
        await deleteBaseUnit({ baseUnitId: baseUnit.id }).unwrap();
      } catch (error) {
        console.error('Failed to delete base unit:', error);
        setFormError('Failed to delete base unit. Please try again.');
      }
    }
  };

  const handleWifiConfig = (baseUnit: { id: number; serialNo: number; base_unit_name: string }) => {
    setSelectedBaseUnit(baseUnit);
    setWifiDialogOpen(true);
  };

  const handleCloseWifiDialog = () => {
    setWifiDialogOpen(false);
    setSelectedBaseUnit(null);
  };

  const handleTelemetryClick = (baseUnit: { id: number; serialNo: number; base_unit_name: string; type: number }) => {
    setSelectedTelemetryBaseUnit(baseUnit);
    setTelemetryDialogOpen(true);
  };

  const handleCloseTelemetryDialog = () => {
    setTelemetryDialogOpen(false);
    setSelectedTelemetryBaseUnit(null);
  };

  const handleArbSensors = (baseUnit: { serialNo: number; base_unit_name: string; silent_alarm?: boolean }) => {
    setSelectedArbUnit(baseUnit);
    setArbSensorDialogOpen(true);
  };

  const handleCloseArbSensorDialog = () => {
    setArbSensorDialogOpen(false);
    setSelectedArbUnit(null);
  };

  const handleAlarmSettings = (baseUnit: { serialNo: number; base_unit_name: string; type: number }) => {
    setSelectedAlarmUnit(baseUnit);
    setAlarmDialogOpen(true);
  };

  const handleCloseAlarmDialog = () => {
    setAlarmDialogOpen(false);
    setSelectedAlarmUnit(null);
  };

  const networkButtonClassName =
    'inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md border border-indigo-200 dark:border-indigo-700 bg-indigo-50/70 dark:bg-indigo-900/30 text-xs font-medium text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 shadow-sm transition-colors';

  if (isLoading || typesLoading) {
    return <div className="p-6">Loading base units...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load base units</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6 flex justify-between items-center">
        <div className="min-w-0 max-w-3xl">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Base Units</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Manage your base units and gateways.
          </p>
          <p className="mt-2 flex items-start gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">
            <Info className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400 mt-0.5" aria-hidden />
            <span>
              <span className="font-medium text-gray-800 dark:text-gray-200">Tip:</span> click a base name, table row, or card
              (not the Network or Delete controls) to open details —{' '}
              <span className="font-medium text-gray-800 dark:text-gray-200">live telemetry</span> is for TS1 bases only.
              {canManageBaseUnits ? (
                <>
                  {' '}
                  On TS1, use <span className="font-medium text-gray-800 dark:text-gray-200">Network</span> for Wi‑Fi, Ethernet, or a network reset.
                </>
              ) : null}
            </span>
          </p>
        </div>
        {canManageBaseUnits && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setQrModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
              title="Generate provisioning QR code for the mobile app"
            >
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">Setup QR</span>
            </button>
            <button
              onClick={() => setIsAddingBaseUnit(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md flex items-center text-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Add Base Unit</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        )}
      </div>

      {formError && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        </div>
      )}

      {isAddingBaseUnit && canManageBaseUnits && (
        <div className="mb-6 bg-app-panel dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Base Unit</h3>

          <div className="mb-6 p-4 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/80">
            <div className="flex gap-3">
              <Smartphone className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Easier setup with {TS1_PORTAL.displayName}
                </h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">
                  Install the {TS1_PORTAL.displayName} app on your phone (Android or iOS). Use Bluetooth to connect to
                  the base unit and configure Wi-Fi, Ethernet network details, and the customer ID the unit should
                  publish to — before or after you register it here.
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {TS1_PORTAL.androidStoreUrl ? (
                    <a
                      href={TS1_PORTAL.androidStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                    >
                      Google Play
                      <ExternalLink className="h-3 w-3 opacity-70" aria-hidden />
                    </a>
                  ) : null}
                  {TS1_PORTAL.iosStoreUrl ? (
                    <a
                      href={TS1_PORTAL.iosStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-white border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                    >
                      App Store
                      <ExternalLink className="h-3 w-3 opacity-70" aria-hidden />
                    </a>
                  ) : null}
                  {!TS1_PORTAL.androidStoreUrl && !TS1_PORTAL.iosStoreUrl ? (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Direct store links will appear here when available — search for{' '}
                      <span className="font-medium text-gray-800 dark:text-gray-200">{TS1_PORTAL.displayName}</span> on
                      Google Play or the App Store.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Serial Number
              </label>
              <input
                type="text"
                required
                value={formData.serialNo}
                onChange={(e) => setFormData({ ...formData, serialNo: e.target.value })}
                placeholder="e.g. 40002747 or 40-00-27-47"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter as printed on the label — dashes are accepted and stripped automatically.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Base Unit Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Base Unit Type
              </label>
              <select
                required
                value={formData.typeId}
                onChange={(e) => setFormData({ ...formData, typeId: e.target.value === '' ? '' : Number(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              >
                <option value="">Select a type</option>
                {types?.map((type) => (
                  <option key={type.type} value={type.type}>
                    {type.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingBaseUnit(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Add Base Unit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Mobile: card list ── */}
      <div className="md:hidden space-y-2">
        {baseUnits?.map((baseUnit) => (
          <div key={baseUnit.id} className="bg-app-panel dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 shadow-sm">
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div
                className="flex items-start gap-3 min-w-0 flex-1 cursor-pointer"
                onClick={() => handleTelemetryClick(baseUnit)}
              >
                <Radio className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  {editingBaseUnit.id === baseUnit.id && canManageBaseUnits ? (
                    <form onSubmit={handleSaveEdit} className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingBaseUnit.name}
                        onChange={e => setEditingBaseUnit({ ...editingBaseUnit, name: e.target.value })}
                        className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full"
                        autoFocus
                      />
                      <button type="submit" className="text-green-600 dark:text-green-400 p-1 flex-shrink-0">✓</button>
                      <button type="button" onClick={handleCancelEdit} className="text-red-500 dark:text-red-400 p-1 flex-shrink-0">✕</button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{baseUnit.base_unit_name}</span>
                      {canManageBaseUnits && (
                        <button
                          onClick={e => { e.stopPropagation(); handleEdit(baseUnit); }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                        >
                          <PencilLine className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 font-mono">{baseUnit.serialNo}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <CommsCell serialNo={baseUnit.serialNo} type={baseUnit.type} lastContact={baseUnit.last_contact ?? null} />
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {types?.find(t => t.type === baseUnit.type)?.description || 'Unknown'}
                    </span>
                  </div>
                  {baseUnit.type === 1 && (
                    <p className="mt-1.5 text-[10px] text-indigo-600/90 dark:text-indigo-400/90 font-medium">
                      Tap this area for telemetry
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {baseUnit.type === 1 && canManageBaseUnits && (
                  <button
                    type="button"
                    className={networkButtonClassName}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWifiConfig(baseUnit);
                    }}
                    title="Configure Wi‑Fi, Ethernet, or reset network on this TS1 base"
                    aria-label="Configure network: Wi-Fi and Ethernet for this TS1 base"
                  >
                    <Router className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Network</span>
                  </button>
                )}
                {baseUnit.type === 3 && canManageBaseUnits && (
                  <button
                    type="button"
                    className={networkButtonClassName}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArbSensors(baseUnit);
                    }}
                    title="Assign sensors to this ARB alarm unit"
                  >
                    <Bell className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Sensors</span>
                  </button>
                )}
                {baseUnit.type !== 3 && canManageBaseUnits && (
                  <button
                    type="button"
                    className={networkButtonClassName}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAlarmSettings(baseUnit);
                    }}
                    title="Configure alarms for this base unit"
                  >
                    <Bell className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Alarms</span>
                  </button>
                )}
                {canManageBaseUnits && (
                  <button
                    type="button"
                    className="p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(baseUnit);
                    }}
                    title="Delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {(!baseUnits || baseUnits.length === 0) && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8 text-sm">No base units registered.</p>
        )}
      </div>

      {/* ── Desktop: table ── */}
      <div className="hidden md:block bg-app-panel dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/3">Base Unit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Comms</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Contact</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-app-panel dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {baseUnits?.map((baseUnit) => (
              <tr
                key={baseUnit.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => handleTelemetryClick(baseUnit)}
                title={
                  baseUnit.type === 1
                    ? 'Open live telemetry — click anywhere on this row except Actions'
                    : 'View base details (telemetry is available for TS1 only)'
                }
              >
                <td className="px-6 py-3">
                  <div className="flex items-center">
                    <Radio className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                        {editingBaseUnit.id === baseUnit.id && canManageBaseUnits ? (
                          <form onSubmit={handleSaveEdit} className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editingBaseUnit.name}
                              onChange={e => setEditingBaseUnit({ ...editingBaseUnit, name: e.target.value })}
                              className="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                            />
                            <button type="submit" className="text-green-600 dark:text-green-400 p-1">✓</button>
                            <button type="button" onClick={handleCancelEdit} className="text-red-600 dark:text-red-400 p-1">✕</button>
                          </form>
                        ) : (
                          <>
                            <span
                              className={
                                baseUnit.type === 1
                                  ? 'hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors underline decoration-dotted decoration-gray-300 dark:decoration-gray-600 underline-offset-2'
                                  : 'hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors'
                              }
                            >
                              {baseUnit.base_unit_name}
                            </span>
                            {canManageBaseUnits && (
                              <button onClick={e => { e.stopPropagation(); handleEdit(baseUnit); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 ml-2">
                                <PencilLine className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{baseUnit.serialNo}</div>
                      {baseUnit.type === 1 && editingBaseUnit.id !== baseUnit.id && (
                        <div className="text-[11px] text-indigo-600 dark:text-indigo-400 mt-1 font-medium">
                          Click row for telemetry
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {types?.find(t => t.type === baseUnit.type)?.description || 'Unknown'}
                </td>
                <td className="px-6 py-3 whitespace-nowrap">
                  <CommsCell serialNo={baseUnit.serialNo} type={baseUnit.type} lastContact={baseUnit.last_contact ?? null} />
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {baseUnit.last_contact ? formatDateTime(new Date(baseUnit.last_contact)) : 'Never'}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end items-center gap-1">
                    {baseUnit.type === 1 && canManageBaseUnits && (
                      <button
                        type="button"
                        className={networkButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWifiConfig(baseUnit);
                        }}
                        title="Configure Wi‑Fi, Ethernet, or reset network on this TS1 base"
                        aria-label="Configure network: Wi-Fi and Ethernet for this TS1 base"
                      >
                        <Router className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Network</span>
                      </button>
                    )}
                    {baseUnit.type !== 3 && canManageBaseUnits && (
                      <button
                        type="button"
                        className={networkButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAlarmSettings(baseUnit);
                        }}
                        title="Configure alarms for this base unit"
                      >
                        <Bell className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Alarms</span>
                      </button>
                    )}
                    {baseUnit.type === 3 && canManageBaseUnits && (
                      <button
                        type="button"
                        className={networkButtonClassName}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArbSensors(baseUnit);
                        }}
                        title="Assign sensors to this ARB alarm unit"
                      >
                        <Bell className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Sensors</span>
                      </button>
                    )}
                    {canManageBaseUnits && (
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-900 dark:text-red-400 p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(baseUnit);
                        }}
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <WifiConfigDialog
        isOpen={wifiDialogOpen}
        onClose={handleCloseWifiDialog}
        baseUnit={selectedBaseUnit}
      />

      <BaseUnitTelemetryDialog
        isOpen={telemetryDialogOpen}
        onClose={handleCloseTelemetryDialog}
        baseUnit={selectedTelemetryBaseUnit}
      />

      <ArbSensorDialog
        isOpen={arbSensorDialogOpen}
        onClose={handleCloseArbSensorDialog}
        baseUnit={selectedArbUnit}
      />

      <BaseUnitAlarmDialog
        isOpen={alarmDialogOpen}
        onClose={handleCloseAlarmDialog}
        baseUnit={selectedAlarmUnit}
      />

      {qrModalOpen && (
        <ProvisioningQRModal onClose={() => setQrModalOpen(false)} />
      )}
    </div>
  );
}